import React from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import AgentsBase from './agents';
import AgentService from '../../../services/agentService';
import { validateAgent } from '../../../validation/agentValidation';
import AgentErrorBoundary from '../../../components/agent/ErrorBoundary';
import {
  setAdminInput,
  getAdminDataFromList,
  resetAdminInput,
  toggleAdminEdit,
  adminActionAlert,
  adminActionStatus,
  changeAdminView,
  setAdminListFilter
} from '../../../redux/actions/admin';
import {
  getAdminListSelector,
  agentListSelector,
  groupAgentsSelector
} from '../../../redux/selectors/admin';
import { fetchAdminList, fetchAgentData, setAgentData, fetchAgentGroups, saveAgentGroups, setAdminData, doActivateAgent, doDeactivateAgent, removeAdminData, agentUploadListAttachment } from '../../../redux/actions/async/admin';
import { onlyActiveAreasSelector } from '../../../redux/selectors/workflow';
import {
	allowMultipleChatbotProvidersMemo,
	providersMemo
} from '../../../redux/selectors/chatbot';
import { timeZoneListSelector } from '../../../redux/selectors/statistics';
import { M_AGENTS, emptyArray } from '../../../common/v5/constants';
import { I } from '../../../common/v5/config';

// Import access key actions and selectors
import {
  list as listAdminAgentAccessKeys,
  create as createAdminAgentAccessKey,
  deleteKey as deleteAdminAgentAccessKey
} from '../../../redux/actions/async/agentAccessKey';
import { adminAgentAccessKeysSelector } from '../../../redux/selectors/agentAccessKey';
import { adminAgentAccessKeyURL } from '../../../redux/actions/async/ajax';

// Import skills actions and selectors
import {
  saveAgentSkill,
  fetchSkillProficiency,
  fetchSkills,
  removeSkillAgent
} from '../../../redux/actions/async/admin';
import {
  skillProficiencySelector,
  skillsAdminSelector
} from '../../../redux/selectors/admin';
import { updateAgentSkill } from '../../../redux/actions/admin';
import { getWorkflowSettingsData } from '../../../redux/selectors/workflow';
import { isSysAdmin } from '../../../common/v5/helpers';
import { onceAgentAreas, onceConnectedAgentAreas } from '../../../redux/actions/async/workflow';

const FORM_EDIT = 'edit';
const FORM_UPLOAD = 'upload';

const mapStateToProps = (state, props) => {
  const server = state.server;
  const app = state.app;
  const admin = app.admin;
  const ui = admin.ui;
  const adminSt = admin.admin;
  const agentData = admin.agentData;
  
  // Get access key data for the current agent
  const accessKeyData = adminAgentAccessKeysSelector(state, props);

  
  // Get skills data
  const availableSkills = skillsAdminSelector(state);
  const proficiencyList = skillProficiencySelector(state);

  // Get current user data for sysadmin checks
  const workflowSettings = getWorkflowSettingsData(state);
  const isSysadmin = isSysAdmin(workflowSettings);
  
  return {
    view: ui.view,
    list: agentListSelector(state),
    input: {
      ...adminSt.input,
      avatarPreview: (() => {
        const avatar = adminSt.avatarPreview;
        if (!avatar || avatar === '' || avatar === 'undefined') {
          return null;
        }
        // Ensure it's a string
        return String(avatar);
      })(),
      // Get the active status from the agent list if we're editing an existing agent
      ...(adminSt.activeId > 0 && {
        active: (() => {
          const agentList = agentListSelector(state);
          const currentAgent = agentList.find(agent => agent.id === adminSt.activeId);
          return currentAgent ? currentAgent.status : undefined;
        })()
      })
    },
    showInline: adminSt.showInline,
    activeId: adminSt.activeId,
    formType: FORM_EDIT,
    adminStatus: adminSt.adminStatus,
    areaList: onlyActiveAreasSelector(state),
    availableAreas: agentData.data?.availableAreas || emptyArray,
    langSrc: adminSt.langSrc || agentData.data?.langSrc || emptyArray,
    interfaceLangSrc: adminSt.interfaceLangSrc || agentData.data?.interfaceLangSrc || emptyArray,
    timeZone: agentData.data?.timeZone || adminSt.timeZone || timeZoneListSelector(state, props) || emptyArray,
    salutationList: adminSt.salutationList || emptyArray,
    signatureList: adminSt.signatureList || emptyArray,
    // Add API data for options that were previously hardcoded
    adminLevelSrc: agentData.data?.adminLevelSrc || emptyArray,
    // Group-related props
    groupsAgentList: groupAgentsSelector(state),
    groupAgentListReady: adminSt.groupAgentListReady || false,
    // Pass the current admin status for monitoring save completion
    currentAdminStatus: adminSt.adminStatus,
    // Access key props
    accessKeyData: accessKeyData,
    // Access key URL creator function
    accessKeyURLCreator: adminAgentAccessKeyURL,

    availableSkills: availableSkills,
    proficiencyList: proficiencyList,
    // Pass sysadmin status to component
    isSysadmin: isSysadmin,
    chatbotProviders: providersMemo(state),
    multipleChatbotProviders: allowMultipleChatbotProvidersMemo(state),
    features: server.features || {}
  };
};

const mapDispatchToProps = (dispatch, props) => {
  const view = M_AGENTS;
  
  return {
    onLoad: () => {
      // Clear any existing search results when loading
      dispatch({
        type: 'AGENT_SEARCH_RESULTS',
        payload: null
      });
      dispatch(changeAdminView(view));
      // Load with default filter (0 = Active agents only, same as old implementation)
      const defaultFilter = { filter: 0 };
      dispatch(fetchAdminList(view, defaultFilter));
    },
    
    onLoadToEdit: (id) => {
      if (id > 0) {
        dispatch(resetAdminInput());
        // Reset avatar preview before loading new agent data
        dispatch(setAdminInput('avatarPreview', ''));
        dispatch(onceAgentAreas());
        dispatch(onceConnectedAgentAreas());
        dispatch(fetchAgentData(id + '/false'));
      } else {
        // Reset avatar preview for new agent
        dispatch(setAdminInput('avatarPreview', ''));
        const defaultAgent = AgentService.createDefaultAgent();
        dispatch(resetAdminInput());

        let fieldsSet = 0;
        Object.keys(defaultAgent).forEach(key => {
          const value = defaultAgent[key];
          // Set all values including false and 0 for preferences
          if (value !== '' && value !== null && value !== undefined) {
            dispatch(setAdminInput(key, value));
            fieldsSet++;
          }
        });
      }
      dispatch(toggleAdminEdit(id, true, false, true));
    },
    
    onLoadToUpload: () => {
      dispatch(toggleAdminEdit(0, true, false, false));
    },
    
    onChangeAdminInput: (field, value) => {
      dispatch(setAdminInput(field, value));
    },
    
    onSaveAdmin: (inputData) => {
      
      // Use the same save logic as the existing admin system
      dispatch(adminActionStatus({ status: 1, msg: I('Saving agent...') }));
      
      // Fix field mappings before saving if inputData is provided
      if (inputData) {
        // Apply field mappings for known inconsistencies
        const fieldMappings = {
          // Map form field names to API field names
          solidusAgentId: 'solidusAgentID',
          chatAlias: 'chatName',
          allowedIP: 'allowedIp',
          playSoundNewChatMessage: 'playSound',
          allowedSimultaneousChatSessions: 'allowedChatAmount',
          useHotkeys: 'hotkeys',
          showHotkeysMarkers: 'hotkeys_markers',
          'hotkeysModifiers.ctrl': 'hotkeys_ctrl',
          'hotkeysModifiers.alt': 'hotkeys_alt', 
          'hotkeysModifiers.shift': 'hotkeys_shift',
          'hotkeysModifiers.meta': 'hotkeys_meta'
        };
        
        // CRITICAL: Ensure the active field is preserved
        if (inputData.active !== undefined) {
          dispatch(setAdminInput('active', inputData.active));
        }
        
        // Handle darkModeAppearance to isDark conversion
        if (inputData.darkModeAppearance !== undefined) {
          dispatch(setAdminInput('darkModeAppearance', inputData.darkModeAppearance));
          const isDark = inputData.darkModeAppearance === 1;
          dispatch(setAdminInput('isDark', isDark));
        }
        
        // Apply field mappings
        Object.keys(fieldMappings).forEach(formField => {
          const apiField = fieldMappings[formField];
          if (formField.includes('.')) {
            // Handle nested fields like hotkeysModifiers.ctrl
            const [parent, child] = formField.split('.');
            if (inputData[parent] && inputData[parent][child] !== undefined) {
              dispatch(setAdminInput(apiField, inputData[parent][child]));
            }
          } else if (inputData[formField] !== undefined) {
            dispatch(setAdminInput(apiField, inputData[formField]));
          }
        });
      }
      
      dispatch(setAdminData(view));
      
    },
    
    onDeleteFromList: (id) => {
      // Handle agent removal using the same logic as other admin views
      dispatch(removeAdminData({id: id}, view));
    },
    
    onCloseForm: () => {
      // Reset avatar preview before closing form
      dispatch(setAdminInput('avatarPreview', ''));
      dispatch(toggleAdminEdit(0, false, false, false));
      dispatch(resetAdminInput());
    },
    
    onFetchAdminList: () => {
      // Use default filter (0 = Active agents) when refreshing list
      const defaultFilter = { filter: 0 };
      dispatch(fetchAdminList(view, defaultFilter));
    },
    
    // Filter change handler - fetches agents with filter parameter
    onFilterChange: (filterValue) => {
      // First, update the Redux state with the new filter value
      dispatch(setAdminListFilter('agentTypeFilter', filterValue));
      
      // Then dispatch action to fetch filtered agent list
      const paramObject = { filter: filterValue };
      dispatch(fetchAdminList(view, paramObject));
    },
    
    // Group-related actions
    onGetAgentGroups: (agentId) => {
      dispatch(fetchAgentGroups(agentId));
    },
    
    onSaveAgentGroups: (agentId, memberIds, teamLeaderIds) => {
      dispatch(saveAgentGroups(agentId, memberIds, teamLeaderIds));
    },
    
    // Access key actions
    onCreateAgentAccessKey: (agentID, params) => {
      console.log('=== Creating access key for agent:', agentID, 'with params:', params);
      return dispatch(createAdminAgentAccessKey(agentID, params));
    },
    
    onDeleteAgentAccessKey: (agentID, accessKeyID) => {
      return dispatch(deleteAdminAgentAccessKey(agentID, accessKeyID));
    },
    
    onLoadAgentAccessKeys: (agentID) => {
      console.log('=== Loading access keys for agent:', agentID);
      return dispatch(listAdminAgentAccessKeys(agentID));
    },
    
    // Skills actions
    onLoadSkills: () => {
      dispatch(fetchSkillProficiency());
      dispatch(fetchSkills());
    },
    
    onSaveAgentSkill: (params) => {
      console.log('=== Saving agent skill with params:', params);
      return dispatch(saveAgentSkill(params));
    },
    
    onRemoveAgentSkill: (params) => {
      console.log('=== Removing agent skill with params:', params);
      return dispatch(removeSkillAgent(params));
    },
    
    onUpdateAgentSkill: (skill, isRemove) => {
      console.log('=== Updating agent skill:', skill, 'isRemove:', isRemove);
      return dispatch(updateAgentSkill(skill, isRemove));
    },
    
    // Agent activation/deactivation
    onActivateAgent: (status, id) => {
      console.log('=== Activating/Deactivating agent:', id, 'status:', status);
      if (status) {
        return dispatch(doActivateAgent(id));
      } else {
        return dispatch(doDeactivateAgent(id));
      }
    },
    
    // Agent search functionality - exactly like the old working system
    onSearchAgents: (searchText, filterValue = 0) => {
      return new Promise((resolve, reject) => {
        $.get('/admin/agents/list', {
          "searchText": searchText || '',
          "filter": filterValue
        })
        .done((data) => {
          resolve(data);
        })
        .fail((error) => {
          console.error('=== Search failed:', error);
          reject(error);
        });
      });
    },
    
    // Agent upload functionality
    onUploadAgents: (files) => {
      console.log('=== Uploading agents with files:', files);
      dispatch(agentUploadListAttachment(files));
    }
  };
};

const withAgents = compose(
  connect(mapStateToProps, mapDispatchToProps)
);

// Wrap with error boundary for better error handling
const AgentsCtnr = (props) => (
  <AgentErrorBoundary context="agent-list">
    {React.createElement(withAgents(AgentsBase), props)}
  </AgentErrorBoundary>
);

export default AgentsCtnr; 